import { createFileRoute } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { MessageCircle, Mail, Clock, ChevronDown, Send } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useState } from "react";

export const Route = createFileRoute("/support")({
  component: SupportPage,
  head: () => ({
    meta: [
      { title: "Support Client — angle-shop.com" },
      { name: "description", content: "Besoin d'aide ? Notre équipe est disponible 7j/7 par chat, email ou téléphone. FAQ, suivi de commande et support technique." },
      { property: "og:title", content: "Support Client — angle-shop.com" },
      { property: "og:description", content: "Notre équipe est disponible 7j/7 pour répondre à vos questions." },
      { property: "og:url", content: "https://shopb.life/support" },
    ],
    links: [
      { rel: "canonical", href: "https://shopb.life/support" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.question,
            acceptedAnswer: { "@type": "Answer", text: f.answer },
          })),
        }),
      },
    ],
  }),
});

const faqs = [
  {
    question: "What are the delivery times?",
    answer: "We ship within 24 to 48 business hours. Standard delivery takes between 5 and 10 business days. If your order is not delivered within 30 days, you get a full refund.",
  },
  {
    question: "How does the '30-day refund' guarantee work?",
    answer: "If your package is not delivered within 30 days of your order, we refund you in full with no conditions. Simply contact us by email or through the form below with your order number.",
  },
  {
    question: "Are the products compatible with my car?",
    answer: "Our products are universal and compatible with all vehicles equipped with a 12V cigarette lighter socket. Check the product page for specific compatibility details.",
  },
  {
    question: "How do I install the products?",
    answer: "All our products are plug & play and install in under 10 minutes with no tools. An installation guide is included with each product and video tutorials are available on request.",
  },
  {
    question: "How do I return a product?",
    answer: "You have 30 days after receipt to return a product. Contact our support team to get a prepaid return label. The refund is processed within 5 to 7 business days after receiving the return.",
  },
  {
    question: "Can I track my order?",
    answer: "Yes, a tracking number is sent to you by email as soon as your package is shipped. You can track your delivery in real time via the link provided.",
  },
];

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-border rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-muted/50 transition-colors"
      >
        <span className="font-medium text-foreground text-sm">{question}</span>
        <ChevronDown className={`w-4 h-4 text-muted-foreground shrink-0 ml-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-6 pb-4">
          <p className="text-sm text-muted-foreground leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
}

function SupportPage() {
  const [formData, setFormData] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <main className="min-h-screen bg-background">
      <Header />

      {/* Hero */}
      <section className="bg-gradient-hero py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-foreground">
              How can we <span className="text-gradient-electric">help you</span>?
            </h1>
            <p className="mt-4 text-muted-foreground text-base sm:text-lg max-w-xl mx-auto">
              Our team is available 7 days a week to answer all your questions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact channels */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { icon: Mail, title: "Email", text: "nessimselmane49@gmail.com", sub: "Response within 24h" },
            { icon: MessageCircle, title: "Chat", text: "Available on site", sub: "Instant response" },
            { icon: Clock, title: "Hours", text: "7 days a week", sub: "9am – 9pm" },
          ].map((c, i) => (
            <motion.div
              key={c.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
              className="bg-card rounded-2xl p-6 shadow-card text-center"
            >
              <div className="w-12 h-12 rounded-xl bg-electric/10 flex items-center justify-center mx-auto mb-3">
                <c.icon className="w-5 h-5 text-electric" />
              </div>
              <h3 className="font-display font-semibold text-foreground">{c.title}</h3>
              <p className="text-sm text-foreground mt-1">{c.text}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{c.sub}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-foreground text-center mb-8">
          Frequently Asked Questions
        </h2>
        <div className="space-y-3">
          {faqs.map((faq) => (
            <FAQItem key={faq.question} question={faq.question} answer={faq.answer} />
          ))}
        </div>
      </section>

      {/* Contact form */}
      <section className="bg-surface py-16 sm:py-20">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-foreground text-center mb-8">
            Contact Us
          </h2>

          {submitted ? (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-12">
              <div className="w-16 h-16 rounded-full bg-electric/10 flex items-center justify-center mx-auto mb-4">
                <Send className="w-7 h-7 text-electric" />
              </div>
              <h3 className="font-display text-xl font-semibold text-foreground">Message sent!</h3>
              <p className="text-sm text-muted-foreground mt-2">We'll get back to you within 24 hours.</p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Name</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-electric"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Email</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-electric"
                    placeholder="your@email.com"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Subject</label>
                <select
                  required
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-electric"
                >
                  <option value="">Choose a subject</option>
                  <option value="order">Order tracking</option>
                  <option value="return">Return / Refund</option>
                  <option value="technical">Technical support</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Message</label>
                <textarea
                  required
                  rows={5}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-electric resize-none"
                  placeholder="Describe your request..."
                />
              </div>
              <button
                type="submit"
                className="w-full py-3 rounded-xl bg-gradient-electric text-electric-foreground font-semibold text-sm shadow-electric hover:opacity-90 transition-opacity"
              >
                Send message
              </button>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </main>
  );
}
